import { Box, CloseButton, Portal, Transition, Overlay, Paper, useMantineTheme } from "@mantine/core";
import OverviewFocusedCard from "./OverviewFocusedCard";
import { OverviewCardProps } from "./types";
import OverviewStandardCard from "./OverviewStandardCard";
import { FocusAreasGroupData } from "@/services/apitypes";
import styles from "./OverviewCard.module.css";
import { useIsMobile } from "@/ui";
import { darkSurfaceOverlayProps } from "@canucci/base-mantine/theme";
import {
  useModalScaleTransition,
  VIEWPORT_WIDTH_RATIO_DESKTOP,
  VIEWPORT_WIDTH_RATIO_MOBILE,
} from "./useModalScaleTransition";

// UI Constants
const MODAL_Z_INDEX = 400;

interface OverviewContainerCardProps extends OverviewCardProps {
  duration: number;
  group: FocusAreasGroupData;
  votingEnabled: boolean;
  isVotingClosed: boolean;
}

function OverviewContainerCard(props: OverviewContainerCardProps) {
  const { group, votingEnabled, isVotingClosed, groupIndex } = props;
  const theme = useMantineTheme();
  const isMobile = useIsMobile();
  const { duration = 200 } = props;

  const { isOpen, isMeasuring, triggerRef, contentRef, isInComplete, handleOpen, handleClose, transitionConfig } =
    useModalScaleTransition({
      duration,
      maxPageWidth: theme.other.sizesInPixels.maxPageWidthDesktop,
      isMobile,
    });

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleOpen();
  };

  return (
    <Box pos="relative" h="100%">
      <Box
        ref={triggerRef}
        className={styles.trigger}
        data-open={isOpen ? "1" : "0"}
        data-testid="focus-area-group-trigger"
        data-group-index={groupIndex}
        h="100%"
      >
        <OverviewStandardCard {...props} onClick={handleClick} />
      </Box>
      <Box onClick={handleClick}>
        <Portal>
          {/* Hidden measuring container - only shown during measurement phase */}
          {isMeasuring && (
            <Box
              ref={contentRef}
              style={{
                position: "fixed",
                visibility: "hidden",
                pointerEvents: "none",
                top: 0,
                left: 0,
                width: Math.min(
                  window.innerWidth * (isMobile ? VIEWPORT_WIDTH_RATIO_MOBILE : VIEWPORT_WIDTH_RATIO_DESKTOP),
                  theme.other.sizesInPixels.maxPageWidthDesktop,
                ),
              }}
            >
              <OverviewFocusedCard
                {...props}
                group={group}
                votingEnabled={votingEnabled}
                isVotingClosed={isVotingClosed}
                isMobile={isMobile}
              />
            </Box>
          )}

          {/* Overlay backdrop */}
          <Transition mounted={isOpen} duration={duration} transition="fade">
            {(transitionStyles) => (
              <Overlay
                {...darkSurfaceOverlayProps}
                zIndex={MODAL_Z_INDEX}
                fixed
                style={transitionStyles}
                onClick={handleClose}
              />
            )}
          </Transition>

          {/* Animated modal - visible during animation, hidden after completion */}
          {!isInComplete && (
            <Transition mounted={isOpen} duration={duration} transition={transitionConfig} timingFunction="ease">
              {(transitionStyle) => (
                <Paper
                  radius="md"
                  className={styles.modalContainer}
                  p={0}
                  style={{
                    ...transitionStyle,
                    overflow: "hidden",
                  }}
                  bg="transparent"
                  onClick={(e) => e.stopPropagation()}
                >
                  <OverviewFocusedCard
                    {...props}
                    group={group}
                    votingEnabled={votingEnabled}
                    isVotingClosed={isVotingClosed}
                    isMobile={isMobile}
                  />
                  <CloseButton onClick={handleClose} variant="transparent" size="lg" className={styles.closeButton} />
                </Paper>
              )}
            </Transition>
          )}

          {/* Responsive modal - shown after animation completes, scales with viewport */}
          {isInComplete && (
            <Paper
              radius="md"
              className={styles.responsiveModal}
              p={0}
              bg="transparent"
              style={
                {
                  "--max-page-width": `${theme.other.sizesInPixels.maxPageWidthDesktop}px`,
                } as React.CSSProperties
              }
              onClick={(e) => e.stopPropagation()}
            >
              <OverviewFocusedCard
                {...props}
                group={group}
                votingEnabled={votingEnabled}
                isVotingClosed={isVotingClosed}
                isMobile={isMobile}
              />
              <CloseButton onClick={handleClose} variant="transparent" size="lg" className={styles.closeButton} />
            </Paper>
          )}
        </Portal>
      </Box>
    </Box>
  );
}

export default OverviewContainerCard;
